import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { publicGetOffer, publicDecideOffer } from '../lib/hrmsApi';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useToast } from '../hooks/use-toast';
import { CheckCircle2, XCircle, Loader2, FileDown } from 'lucide-react';
import { generateOfferPdf } from '../lib/offerPdf';
import { company } from '../mock';

const STATUS_COLOR = { sent: 'bg-blue-500', accepted: 'bg-emerald-500', rejected: 'bg-red-500', expired: 'bg-amber-500', revoked: 'bg-slate-500', draft: 'bg-slate-500' };

const PublicOfferPage = () => {
  const { toast } = useToast();
  const { token } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    try { setOffer(await publicGetOffer(token)); } catch (e) { setError(e?.response?.data?.detail || 'Offer not found'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [token]);

  const decide = async (decision) => {
    if (decision === 'rejected' && !reason.trim()) {
      toast({ title: 'Please share a short reason for declining' });
      return;
    }
    setBusy(true);
    try {
      await publicDecideOffer(token, decision, reason);
      toast({ title: `Offer ${decision}`, description: decision === 'accepted' ? 'The HR team has been notified. Welcome aboard!' : 'Thank you for letting us know.' });
      await load();
    } catch (err) {
      toast({ title: 'Action failed', description: err?.response?.data?.detail || '' });
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading offer…</div>;
  if (error) return (
    <div className="min-h-screen flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <XCircle className="h-12 w-12 mx-auto text-red-500" />
        <h1 className="mt-4 text-xl font-bold">{error}</h1>
        <p className="text-sm text-slate-500 mt-2">The link may be incorrect or the offer has been revoked. Please contact <a className="text-orange-600" href="mailto:info@hiprotechnologies.com">info@hiprotechnologies.com</a>.</p>
        <Link to="/" className="mt-4 inline-block text-sm text-slate-600 hover:text-orange-600">← Back to Hipro Technologies</Link>
      </div>
    </div>
  );
  if (!offer) return null;

  const readOnly = offer.status !== 'sent';

  return (
    <div className="min-h-screen bg-slate-50" data-testid="public-offer-page">
      <div className="max-w-3xl mx-auto px-5 py-10">
        <div className="flex items-center gap-3 mb-6">
          <img src={company.logo} alt="Hipro" className="h-10 w-10 rounded" />
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Hipro Technologies</div>
            <div className="font-bold text-slate-900">Your offer letter</div>
          </div>
          <div className="ml-auto"><Badge className={STATUS_COLOR[offer.status] || 'bg-slate-500'}>{offer.status}</Badge></div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 shadow-sm p-8">
          <h1 className="text-2xl font-extrabold text-slate-900">Dear {offer.candidate_name},</h1>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">
            We are delighted to offer you the position of <b>{offer.designation}</b>{offer.department ? <> in the <b>{offer.department}</b> team</> : null} at <b>Hipro Technologies</b>.
            Your tentative joining date is <b>{offer.joining_date}</b>{offer.location ? <> and you'll be based in <b>{offer.location}</b></> : null}
            {offer.reporting_manager ? <>, reporting to <b>{offer.reporting_manager}</b></> : null}.
          </p>
          <p className="mt-3 text-sm text-slate-700 leading-relaxed">
            Your annual compensation will be <b>{offer.ctc}</b>. The role has a probation period of <b>{offer.probation_months || 3} months</b> and a notice period of <b>{offer.notice_period_days || 60} days</b>.
          </p>

          <div className="mt-6 grid grid-cols-2 gap-3 text-sm">
            {[
              ['Designation', offer.designation], ['Department', offer.department || '—'],
              ['CTC', offer.ctc], ['Joining date', offer.joining_date],
              ['Location', offer.location || '—'], ['Reporting manager', offer.reporting_manager || '—'],
              ['Offer ID', offer.offer_id], ['Valid until', (offer.expires_at || '').split('T')[0]],
            ].map(([k, v]) => (
              <div key={k} className="rounded-lg bg-slate-50 border border-slate-200 p-3">
                <div className="text-[11px] uppercase tracking-widest text-slate-500">{k}</div>
                <div className="text-slate-900 font-semibold">{v || '—'}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex items-center gap-2">
            <Button variant="outline" onClick={() => generateOfferPdf(offer)} data-testid="public-offer-pdf"><FileDown className="h-4 w-4 mr-1" /> Download PDF</Button>
          </div>

          {!readOnly && (
            <>
              <div className="mt-8 border-t border-slate-200 pt-6">
                <h2 className="text-lg font-bold text-slate-900">Your response</h2>
                <p className="text-sm text-slate-500">If declining, please share a short reason so the team can follow up.</p>
                <Textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="mt-3" placeholder="Optional note (required if declining)…" data-testid="public-offer-reason" />
                <div className="mt-4 flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => decide('accepted')} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 h-11" data-testid="public-offer-accept">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CheckCircle2 className="h-4 w-4 mr-1" /> Accept offer</>}
                  </Button>
                  <Button onClick={() => decide('rejected')} disabled={busy} variant="outline" className="border-red-300 text-red-600 hover:bg-red-50 h-11" data-testid="public-offer-reject">
                    <XCircle className="h-4 w-4 mr-1" /> Decline offer
                  </Button>
                </div>
              </div>
            </>
          )}
          {readOnly && offer.status === 'accepted' && <div className="mt-8 rounded-xl bg-emerald-50 border border-emerald-200 p-5 text-emerald-800">🎉 You've accepted this offer. Our team will reach out with onboarding details shortly.
            {offer.signature_block?.signature && (
              <div className="mt-3 text-xs text-emerald-900/80 font-mono break-all">
                <div className="font-semibold text-emerald-900 not-mono">Digitally signed · audit trail embedded in PDF</div>
                <div>Signature: {offer.signature_block.signature.slice(0, 40)}…</div>
                <div>Document hash: {offer.signature_block.document_hash.slice(0, 40)}…</div>
              </div>
            )}
          </div>}
          {readOnly && offer.status === 'rejected' && <div className="mt-8 rounded-xl bg-red-50 border border-red-200 p-5 text-red-700">You have declined this offer. We wish you the very best.</div>}
          {readOnly && offer.status === 'expired' && <div className="mt-8 rounded-xl bg-amber-50 border border-amber-200 p-5 text-amber-800">This offer has expired. Please contact us to reactivate.</div>}
          {readOnly && offer.status === 'revoked' && <div className="mt-8 rounded-xl bg-slate-50 border border-slate-200 p-5 text-slate-700">This offer has been revoked.</div>}
        </div>

        <div className="text-center text-xs text-slate-500 mt-8">Hipro Technologies · info@hiprotechnologies.com</div>
      </div>
    </div>
  );
};

export default PublicOfferPage;
